import { OneCancelSchedulePostDocument, OneUpdatePostDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import { OneCreateAction, OneCreationActionResolver } from 'modules/core/lib/oneCreateActions/types'
import { POSTS_POLICIES } from 'modules/posts/lib/postsPolicies'

const useSchedulePostActionResolver: OneCreationActionResolver = () => {
  const { navigation } = useNavigation()

  const [updatePost, { loading: scheduleLoading }] = useMutation(OneUpdatePostDocument, {
    extractorFn: (data) => data.postsMutations.updatePostsPost,
    errorPolicy: 'all',
  })

  const [cancelSchedulePost, { loading: cancelLoading }] = useMutation(
    OneCancelSchedulePostDocument,
    {
      extractorFn: (data) => data.postsMutations.cancelSchedulePostsPost,
      errorPolicy: 'all',
    }
  )

  const schedulePostHandler = async (args: {
    id: string
    action: 'schedule' | 'cancelSchedule'
    publishedAt?: string | null
  }) => {
    if (args.action === 'cancelSchedule') {
      const result = await cancelSchedulePost({
        variables: { id: args.id },
      })

      const handled = handleResult(result, (_errors) => {})

      if (handled.success && handled.data.post?.id) {
        return {
          to: navigation.communities.post.id.fullPath({ id: handled.data.post.id }),
          data: args,
        }
      }

      return handled
    }

    if (args.action === 'schedule' && !args.publishedAt) {
      return {
        success: false as const,
        errors: [
          { message: 'publishedAt is required when scheduling a post', type: 'validation_error' },
        ],
      }
    }

    // action === 'schedule' — use updatePostsPost with publishedAt
    const result = await updatePost({
      variables: {
        id: args.id,
        publishedAt: args.publishedAt ?? undefined,
      },
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success && handled.data.post?.id) {
      return {
        to: navigation.communities.post.id.fullPath({ id: handled.data.post.id }),
        data: args,
      }
    }

    return handled
  }

  return {
    resolver: schedulePostHandler,
    loading: scheduleLoading || cancelLoading,
  }
}

export const SchedulePostAction: OneCreateAction = {
  actionResolver: useSchedulePostActionResolver,
  policies: [POSTS_POLICIES.postsManage],
}
